// ============================================================================
// BrightPath - PushPermissionPrompt Component
// BP-12-03: Notifications Frontend
// ============================================================================

import React, { useState } from 'react';
import { Bell, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface PushPermissionPromptProps {
  onEnable?: () => Promise<void>;
  onDismiss?: () => void;
  variant?: 'banner' | 'card' | 'modal';
  showDismiss?: boolean;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export const PushPermissionPrompt: React.FC<PushPermissionPromptProps> = ({
  onEnable,
  onDismiss,
  variant = 'card',
  showDismiss = true,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const {
    isSupported,
    isEnabled,
    permissionStatus,
    isEnabling,
    enable,
    error,
  } = usePushNotifications();

  // Don't show if not supported, already enabled, or dismissed
  if (!isSupported || isEnabled || isDismissed) {
    return null;
  }

  // Don't show if permission was previously denied
  if (permissionStatus === 'denied') {
    return null;
  }

  const handleEnable = async () => {
    try {
      if (onEnable) {
        await onEnable();
      } else {
        await enable();
      }
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  // Banner variant
  if (variant === 'banner') {
    return (
      <div className="bg-sage/10 border-b border-sage/20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-sage/20 rounded-lg">
                <Bell className="w-4 h-4 text-sage-dark" />
              </div>
              <p className="text-sm text-navy">
                <span className="font-medium">Enable push notifications</span>
                <span className="hidden sm:inline">
                  {' '}to get instant updates about appointments and more
                </span>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleEnable}
                disabled={isEnabling}
                className="
                  px-4 py-1.5 rounded-lg text-sm font-medium
                  bg-sage text-white hover:bg-sage-dark
                  transition-colors disabled:opacity-50
                  flex items-center gap-2
                "
              >
                {isEnabling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enabling...
                  </>
                ) : (
                  'Enable'
                )}
              </button>

              {showDismiss && (
                <button
                  onClick={handleDismiss}
                  className="p-1.5 rounded-lg hover:bg-sage/20 text-warm-gray transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modal variant
  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-lifted animate-fade-in">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-sage/15 flex items-center justify-center">
              <Bell className="w-8 h-8 text-sage" />
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-navy mb-2">
              Stay Updated
            </h3>
            <p className="text-warm-gray">
              Enable push notifications to receive instant updates about appointments,
              medication reminders, and important messages from your care team.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error.message}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleEnable}
              disabled={isEnabling}
              className="
                w-full py-3 rounded-xl text-base font-semibold
                bg-sage text-white hover:bg-sage-dark
                transition-colors disabled:opacity-50
                flex items-center justify-center gap-2
              "
            >
              {isEnabling ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-5 h-5" />
                  Enable Notifications
                </>
              )}
            </button>

            {showDismiss && (
              <button
                onClick={handleDismiss}
                className="
                  w-full py-3 rounded-xl text-base font-medium
                  text-warm-gray hover:text-navy hover:bg-cream
                  transition-colors
                "
              >
                Maybe Later
              </button>
            )}
          </div>

          {/* Privacy note */}
          <p className="mt-4 text-xs text-center text-warm-gray">
            You can change this setting at any time in your account preferences.
          </p>
        </div>
      </div>
    );
  }

  // Card variant (default)
  return (
    <div className="bg-white rounded-xl border border-warm-gray/10 p-5 shadow-soft">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="p-3 bg-sage/15 rounded-xl flex-shrink-0">
          <Bell className="w-6 h-6 text-sage" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-navy mb-1">
            Enable Push Notifications
          </h4>
          <p className="text-sm text-warm-gray mb-4">
            Get instant updates about appointments, medication reminders, and
            important messages from your care team.
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error.message}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleEnable}
              disabled={isEnabling}
              className="
                px-5 py-2 rounded-lg text-sm font-semibold
                bg-sage text-white hover:bg-sage-dark
                transition-colors disabled:opacity-50
                flex items-center gap-2
              "
            >
              {isEnabling ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enabling...
                </>
              ) : (
                'Enable Notifications'
              )}
            </button>

            {showDismiss && (
              <button
                onClick={handleDismiss}
                className="
                  px-4 py-2 rounded-lg text-sm font-medium
                  text-warm-gray hover:text-navy hover:bg-cream
                  transition-colors
                "
              >
                Not Now
              </button>
            )}
          </div>
        </div>

        {/* Close button */}
        {showDismiss && (
          <button
            onClick={handleDismiss}
            className="p-1 rounded-lg hover:bg-cream text-warm-gray transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Success Message Component
// ----------------------------------------------------------------------------

export const PushEnabledMessage: React.FC<{ onDismiss?: () => void }> = ({
  onDismiss,
}) => {
  return (
    <div className="bg-sage/10 border border-sage/20 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-sage/20 rounded-lg">
          <CheckCircle className="w-5 h-5 text-sage" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-navy">
            Push notifications enabled!
          </p>
          <p className="text-xs text-warm-gray">
            You'll receive updates about appointments and messages.
          </p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="p-1 rounded-lg hover:bg-sage/20 text-warm-gray transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PushPermissionPrompt;

